import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, IconButton, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Accordion, AccordionSummary, AccordionDetails,
  Alert, AlertTitle, LinearProgress, InputAdornment, Tooltip, Badge, Snackbar,
  ToggleButton, ToggleButtonGroup, Card, CardContent, Stack, Divider, Grid,
  useMediaQuery, useTheme
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SportsIcon from '@mui/icons-material/Sports';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { PlayerShirtImage } from '../player/player-shirt-image';
import { SiteVarsContext } from '../../contexts/sitevars-context';
import { useSiteVarsFunctions } from '../../hooks/use-site-vars-functions';

// ═══════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════

const STORAGE_KEY = 'tlog_auction_helper_v1';
const TOTAL_BUDGET = 20.00;
const MIN_BID = 0.50;
const BID_INCREMENT = 0.25;
const SQUAD_SIZE = 11;

const FORMATIONS = {
  '442': { GKP: 1, DEF: 4, MID: 4, FWD: 2 },
  '451': { GKP: 1, DEF: 4, MID: 5, FWD: 1 },
  '433': { GKP: 1, DEF: 4, MID: 3, FWD: 3 },
};

const DEFAULT_POSITION_BUDGETS = {
  '442': { GKP: 0.75, DEF: 3.50, MID: 10.00, FWD: 5.75 },
  '451': { GKP: 0.75, DEF: 3.50, MID: 13.50, FWD: 2.25 },
  '433': { GKP: 0.75, DEF: 3.50, MID: 8.00, FWD: 7.75 },
};

const POS_MAP = { 1: 'GKP', 2: 'DEF', 3: 'MID', 4: 'FWD' };
const POS_LABELS = { 1: 'Goalkeepers', 2: 'Defenders', 3: 'Midfielders', 4: 'Forwards' };
const POS_COLORS = { 1: '#f0c808', 2: '#4caf50', 3: '#2196f3', 4: '#f44336' };

// ═══════════════════════════════════════════════════════════════════
// PRE-LOADED RESEARCH DATA — BALANCED BLUEPRINT STRATEGY CALIBRATED
// Target sum for 4-4-2: GK £0.75 + DEF £3.50 + MID £10.00 + FWD £5.75 = £20.00
// ═══════════════════════════════════════════════════════════════════

const RESEARCH = {
  // --- GOALKEEPERS (Target Budget: £0.75) ---
  // Sequence: Raya (Lot #1) -> Pickford (#2) -> Donnarumma (#3) -> Kelleher (#5) -> Martinez (#8) -> Lammens (#10) -> Verbruggen (#22) -> Petrović (#23) -> Leno (#24) -> Kinsky (#28)
  'Lammens':      { value: 'good', maxBid: 0.50, tag: 'backup', notes: '⚡ Lot #10: Early opening bid at £0.50. If outbid, PASS and wait for Verbruggen at Lot #22!' },
  'Verbruggen':   { value: 'excellent', maxBid: 0.75, tag: 'target', notes: '🎯 Lot #22: Primary Balanced Blueprint GK (£4.5m FPL, 130pts). Bid up to £0.75.' },
  'Kinsky':       { value: 'excellent', maxBid: 0.50, tag: 'backup', notes: '🛡️ Lot #28: Downstream fallback for £0.50 if Verbruggen gets contested above £0.75.' },
  'Petrović':     { value: 'good', maxBid: 0.50, tag: 'backup', notes: '🛡️ Lot #23: Bournemouth starting GK fallback at £0.50.' },
  'Leno':         { value: 'good', maxBid: 0.50, tag: 'backup', notes: '🛡️ Lot #24: Fulham #1 starter fallback at £0.50.' },
  'Donnarumma':   { value: 'good', maxBid: 0.75, tag: null, notes: 'Lot #3: Man City #1. Only bid if he drops to £0.75.' },
  'Kelleher':     { value: 'good', maxBid: 0.75, tag: null, notes: 'Lot #5: Brentford #1, 143pts.' },
  'Raya':         { value: 'good', maxBid: 1.00, tag: null, notes: 'Lot #1: Premium GK — only bid if other managers let him slide under £1.00.' },
  'Pickford':     { value: 'average', maxBid: 0.50, tag: null, notes: 'Lot #2: Everton #1.' },
  'Martinez':     { value: 'good', maxBid: 0.50, tag: null, notes: 'Lot #8: Villa #1.' },
  'Roefs':        { value: 'good', maxBid: 0.50, tag: null, notes: 'Lot #6: Sunderland #1.' },
  'Dubravka':     { value: 'average', maxBid: 0.50, tag: null, notes: 'Lot #48: Spurs £4.0m backup.' },

  // --- DEFENDERS (Target Budget: £3.50 across 4 DEFs: £1.25 + £0.75 + £0.75 + £0.75) ---
  'Calafiori':    { value: 'excellent', maxBid: 1.25, tag: 'backup', notes: '⚡ Lot #16: Arsenal nailed defender. Bid up to £1.25; if won, skip Porro!' },
  'Pedro Porro':  { value: 'excellent', maxBid: 1.25, tag: 'target', notes: '🎯 Lot #18: Spurs attacking RB. Primary attacking DEF anchor (bid up to £1.25).' },
  'N.Williams':   { value: 'good', maxBid: 0.75, tag: 'target', notes: '🎯 Lot #28: Forest attacking FB (128pts). Target bid: £0.75.' },
  'Konsa':        { value: 'excellent', maxBid: 0.75, tag: 'target', notes: '🎯 Lot #35: Villa nailed CB (£4.5m FPL). Outstanding value at £0.75.' },
  'Shaw':         { value: 'good', maxBid: 0.75, tag: 'backup', notes: '🛡️ Lot #36: Man Utd LB (£4.5m FPL). Top backup if fit.' },
  'Diop':         { value: 'excellent', maxBid: 0.75, tag: 'target', notes: '🎯 Lot #52: Ipswich £4.0m starting CB. Reliable £0.50-£0.75 enabler.' },
  'van Ewijk':    { value: 'good', maxBid: 0.50, tag: 'backup', notes: '🛡️ Lot #53: Coventry attacking RB (£4.0m). Budget enabler fallback.' },
  'Thomas':       { value: 'good', maxBid: 0.50, tag: 'backup', notes: '🛡️ Lot #54: Coventry £4.0m starting CB. Budget enabler fallback.' },
  'Truffert':     { value: 'good', maxBid: 0.75, tag: 'backup', notes: 'Lot #11: Bournemouth attacking LB (165pts). Value backup at £0.75.' },
  'Muñoz':        { value: 'good', maxBid: 0.75, tag: 'backup', notes: 'Lot #14: Palace attacking RWB. Value backup at £0.75.' },
  'Maguire':      { value: 'good', maxBid: 0.75, tag: null, notes: 'Lot #27: Man Utd £5.0m starter.' },
  'Virgil':       { value: 'excellent', maxBid: 1.50, tag: null, notes: 'Lot #2: Liverpool captain. Only bid if room is quiet under £1.50.' },
  'Gabriel':      { value: 'good', maxBid: 2.00, tag: null, notes: 'Lot #1: Arsenal top DEF. Let others overspend.' },
  'Guéhi':        { value: 'good', maxBid: 1.00, tag: null, notes: 'Lot #5: Man City £6.0m.' },
  'Tarkowski':    { value: 'good', maxBid: 0.75, tag: null, notes: 'Lot #7: Everton, 170pts.' },
  'Senesi':       { value: 'good', maxBid: 0.75, tag: null, notes: 'Lot #6: Spurs, 175pts.' },
  'Cash':         { value: 'good', maxBid: 0.50, tag: null, notes: 'Villa £4.5m.' },
  'Saliba':       { value: 'poor', maxBid: 0, tag: 'avoid', notes: '🔴 BACK SURGERY — out long term. DO NOT BUY.' },

  // --- MIDFIELDERS (Target Budget: £10.00 across 4 MIDs: £5.25 + £2.50 + £1.25 + £1.00) ---
  'B.Fernandes':  { value: 'excellent', maxBid: 5.25, tag: 'target', notes: '👑 Lot #1: Man Utd captain, 235pts! Set pieces + pens. Cornerpiece of team (bid up to £5.25).' },
  'Saka':         { value: 'good', maxBid: 4.50, tag: 'backup', notes: '⚡ Lot #2: Arsenal talisman. Direct premium pivot if Bruno exceeds £5.25!' },
  'Palmer':       { value: 'good', maxBid: 4.00, tag: 'backup', notes: '⚡ Lot #3: Chelsea talisman. Secondary premium pivot at £4.00.' },
  'Gibbs-White':  { value: 'good', maxBid: 1.75, tag: 'backup', notes: '⚡ Lot #5: Forest #10 (188pts). Strong mid-tier pick up to £1.75.' },
  'Mbeumo':       { value: 'good', maxBid: 2.00, tag: 'backup', notes: '⚡ Lot #6: Man Utd £8.0m FPL. Value up to £2.00.' },
  'Rice':         { value: 'good', maxBid: 1.75, tag: 'backup', notes: '⚡ Lot #8: Arsenal (184pts). Alternative if Szoboszlai is contested.' },
  'Szoboszlai':   { value: 'excellent', maxBid: 2.50, tag: 'target', notes: '🎯 Lot #14: Liverpool engine (£7.0m FPL, 160pts). Secondary midfield anchor (bid up to £2.50).' },
  'Enzo':         { value: 'good', maxBid: 1.50, tag: 'backup', notes: '🛡️ Lot #15: Chelsea £7.0m starter (157pts). Good backup at £1.50.' },
  'Anderson':     { value: 'excellent', maxBid: 1.25, tag: 'backup', notes: '🛡️ Lot #19: Man City (180pts, £6.5m). Premium value backup for Szoboszlai/Ndiaye.' },
  'Wilson':       { value: 'good', maxBid: 1.00, tag: 'backup', notes: '🛡️ Lot #20: Leeds (168pts, £6.5m). Solid starter backup at £1.00.' },
  'Ndiaye':       { value: 'good', maxBid: 1.25, tag: 'target', notes: '🎯 Lot #23: Everton (£6.0m FPL, 128pts). Key mid-price starter (bid up to £1.25).' },
  'E.Le Fée':     { value: 'good', maxBid: 1.00, tag: 'target', notes: '🎯 Lot #24: Sunderland (£6.0m FPL, 147pts). Budget starter completing mid (bid up to £1.00).' },
  'Groß':         { value: 'good', maxBid: 0.75, tag: 'backup', notes: '🛡️ Lot #34: Brighton £5.5m. Reliable budget mid fallback.' },
  'Semenyo':      { value: 'good', maxBid: 2.00, tag: null, notes: 'Lot #4: Man City winger, 202pts.' },
  'Rogers':       { value: 'average', maxBid: 1.50, tag: null, notes: 'Lot #9: Chelsea £7.5m.' },
  'Wirtz':        { value: 'good', maxBid: 1.50, tag: null, notes: 'Lot #11: Liverpool £7.5m.' },
  'Minteh':       { value: 'poor', maxBid: 0, tag: 'avoid', notes: '🔴 LEG INJURY — out until late Nov. AVOID.' },
  'Doku':         { value: 'average', maxBid: 0.75, tag: 'avoid', notes: '⚠️ Calf injury doubt.' },

  // --- FORWARDS (Target Budget: £5.75 across 2 FWDs: £3.75 + £1.50 + £0.50 flex) ---
  'Watkins':      { value: 'good', maxBid: 3.00, tag: 'backup', notes: '⚡ Lot #4: Villa £8.0m (167pts). Pivot if João Pedro is lost.' },
  'João Pedro':   { value: 'excellent', maxBid: 3.75, tag: 'target', notes: '🎯 Lot #5: Chelsea (£7.5m FPL, 177pts, 5.1 PPG). Premier striker anchor (bid up to £3.75).' },
  'Mateta':       { value: 'good', maxBid: 1.25, tag: 'backup', notes: '🛡️ Lot #11: Palace #9 (114pts). Backup for Calvert-Lewin.' },
  'Calvert-Lewin':{ value: 'good', maxBid: 1.50, tag: 'target', notes: '🎯 Lot #13: Leeds (£6.0m FPL, 142pts). Proven budget starter (bid up to £1.50).' },
  'Brobbey':      { value: 'average', maxBid: 0.75, tag: 'backup', notes: '🛡️ Lot #19: Sunderland £6.0m. Budget striker fallback.' },
  'Isak':         { value: 'good', maxBid: 2.50, tag: null, notes: 'Lot #2: Liverpool £9.0m.' },
  'Gyökeres':     { value: 'good', maxBid: 2.00, tag: null, notes: 'Lot #6: Arsenal £7.5m.' },
  'Haaland':      { value: 'excellent', maxBid: 11.00, tag: null, notes: '⚠️ Haaland Anchor Strategy only. Skip in Balanced Blueprint to protect depth.' },
  'Welbeck':      { value: 'average', maxBid: 0.50, tag: 'avoid', notes: '⚠️ Injury doubt.' },
};

// ═══════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

const getDefaultState = () => ({
  budget: TOTAL_BUDGET,
  mySquad: [],
  soldPlayers: [],
  playerTags: {},
  activePosition: 1,
  targetFormation: '442',
  posBudgets: { GKP: 0.75, DEF: 3.50, MID: 10.00, FWD: 5.75 },
  searchTerm: '',
  auctionStarted: false,
});

const loadState = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        ...getDefaultState(),
        ...parsed,
        posBudgets: parsed.posBudgets || DEFAULT_POSITION_BUDGETS[parsed.targetFormation || '442'] || { GKP: 0.75, DEF: 3.50, MID: 10.00, FWD: 5.75 },
      };
    }
  } catch (e) {
    console.warn('Failed to load auction state from localStorage', e);
  }
  return getDefaultState();
};

const saveState = (state) => {
  try {
    const toSave = {
      budget: state.budget,
      mySquad: state.mySquad,
      soldPlayers: state.soldPlayers,
      playerTags: state.playerTags,
      activePosition: state.activePosition,
      targetFormation: state.targetFormation,
      posBudgets: state.posBudgets,
      auctionStarted: state.auctionStarted,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
  } catch (e) {
    console.warn('Failed to save auction state', e);
  }
};

const getResearchData = (player) => {
  return RESEARCH[player.lastName] || null;
};

const getValueColor = (value) => {
  switch (value) {
    case 'excellent': return '#2e7d32';
    case 'good': return '#1565c0';
    case 'average': return '#6d6d6d';
    case 'poor': return '#c62828';
    default: return '#757575';
  }
};

const getTagColor = (tag) => {
  switch (tag) {
    case 'target': return 'success';
    case 'backup': return 'warning';
    case 'avoid': return 'error';
    default: return 'default';
  }
};

const formatBudget = (amount) => `£${(Math.round(amount * 100) / 100).toFixed(2)}`;

const getPositionCounts = (squad, players) => {
  const counts = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
  squad.forEach(s => {
    const player = players.find(p => p.playerId === s.playerId);
    if (player) {
      const pos = POS_MAP[player.positionId];
      if (pos) counts[pos]++;
    }
  });
  return counts;
};

const isFormationValid = (counts) => {
  if (counts.GKP !== 1) return false;
  if (counts.DEF !== 4) return false;
  if (counts.MID < 3 || counts.MID > 5) return false;
  if (counts.FWD < 1 || counts.FWD > 3) return false;
  if (counts.GKP + counts.DEF + counts.MID + counts.FWD !== 11) return false;
  return true;
};

const getFormationString = (counts) => {
  return `${counts.DEF}-${counts.MID}-${counts.FWD}`;
};

const getPositionsNeeded = (counts, formation) => {
  const target = FORMATIONS[formation];
  return {
    GKP: Math.max(0, target.GKP - counts.GKP),
    DEF: Math.max(0, target.DEF - counts.DEF),
    MID: Math.max(0, target.MID - counts.MID),
    FWD: Math.max(0, target.FWD - counts.FWD),
  };
};


// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const AuctionHelper = ({ players }) => {
  const { getTeamById, getPlayerPositionById } = useSiteVarsFunctions();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // --- STATE ---
  const [state, setState] = useState(() => {
    const initial = loadState();
    // Initialise research-based tags for players that haven't been tagged yet
    const tags = { ...initial.playerTags };
    players.forEach(p => {
      if (!tags[p.playerId]) {
        const research = getResearchData(p);
        if (research && research.tag) {
          tags[p.playerId] = {
            tag: research.tag,
            maxBid: research.maxBid,
            notes: research.notes || '',
          };
        }
      }
    });
    return { ...initial, playerTags: tags };
  });

  const [buyDialogOpen, setBuyDialogOpen] = useState(false);
  const [buyDialogPlayer, setBuyDialogPlayer] = useState(null);
  const [buyAmount, setBuyAmount] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showRecommendations, setShowRecommendations] = useState(true);
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'targets' | 'available'

  // Positional budget customization
  const [editBudgetsOpen, setEditBudgetsOpen] = useState(false);
  const [tempBudgets, setTempBudgets] = useState(() => state.posBudgets || DEFAULT_POSITION_BUDGETS['442']);

  // Persist state on every change
  useEffect(() => {
    saveState(state);
  }, [state]);

  // --- DERIVED VALUES ---
  const squadPlayerIds = useMemo(() => new Set(state.mySquad.map(s => s.playerId)), [state.mySquad]);
  const soldPlayerIds = useMemo(() => new Set(state.soldPlayers), [state.soldPlayers]);
  const posCounts = useMemo(() => getPositionCounts(state.mySquad, players), [state.mySquad, players]);
  const posNeeded = useMemo(() => getPositionsNeeded(posCounts, state.targetFormation), [posCounts, state.targetFormation]);
  const totalNeeded = posNeeded.GKP + posNeeded.DEF + posNeeded.MID + posNeeded.FWD;
  const maxBidAllowed = useMemo(() => {
    const reserveForRemaining = Math.max(0, totalNeeded - 1) * MIN_BID;
    return Math.max(MIN_BID, state.budget - reserveForRemaining);
  }, [state.budget, totalNeeded]);

  // 1. Actual spend by position
  const spentByPos = useMemo(() => {
    const spent = { GKP: 0, DEF: 0, MID: 0, FWD: 0 };
    state.mySquad.forEach(s => {
      const p = players.find(pl => pl.playerId === s.playerId);
      if (p) {
        const pos = POS_MAP[p.positionId];
        if (pos) spent[pos] = Math.round((spent[pos] + (s.bidAmount || 0)) * 100) / 100;
      }
    });
    return spent;
  }, [state.mySquad, players]);

  // 2. Positional Budget Waterfall (Auction Order: GKP -> DEF -> MID -> FWD)
  const budgetWaterfall = useMemo(() => {
    const initial = state.posBudgets || DEFAULT_POSITION_BUDGETS[state.targetFormation] || { GKP: 0.75, DEF: 3.50, MID: 10.00, FWD: 5.75 };

    // Phase 1: GKP
    const gkpTarget = initial.GKP;
    const gkpSpent = spentByPos.GKP;
    const gkpRollover = Math.round((gkpTarget - gkpSpent) * 100) / 100; // positive = saved

    // Phase 2: DEF (adjusted with GKP rollover)
    const defBase = initial.DEF;
    const defAdjusted = Math.max(0, Math.round((defBase + gkpRollover) * 100) / 100);
    const defSpent = spentByPos.DEF;
    const defRollover = Math.round(((gkpTarget + defBase) - (gkpSpent + defSpent)) * 100) / 100;

    // Phase 3: MID (adjusted with GKP + DEF rollover)
    const midBase = initial.MID;
    const midAdjusted = Math.max(0, Math.round((midBase + defRollover) * 100) / 100);
    const midSpent = spentByPos.MID;
    const midRollover = Math.round(((gkpTarget + defBase + midBase) - (gkpSpent + defSpent + midSpent)) * 100) / 100;

    // Phase 4: FWD (adjusted with full remaining balance in bank)
    const fwdBase = initial.FWD;
    const fwdSpent = spentByPos.FWD;
    const fwdAdjusted = Math.max(0, Math.round(state.budget * 100) / 100);

    return {
      GKP: {
        posId: 1,
        posKey: 'GKP',
        label: 'Goalkeepers',
        target: gkpTarget,
        adjustedTarget: gkpTarget,
        spent: gkpSpent,
        rolloverFromPrev: 0,
        rolloverToNext: gkpRollover,
        remainingInPhase: Math.max(0, Math.round((gkpTarget - gkpSpent) * 100) / 100),
      },
      DEF: {
        posId: 2,
        posKey: 'DEF',
        label: 'Defenders',
        target: defBase,
        adjustedTarget: defAdjusted,
        spent: defSpent,
        rolloverFromPrev: gkpRollover,
        rolloverToNext: defRollover,
        remainingInPhase: Math.max(0, Math.round((defAdjusted - defSpent) * 100) / 100),
      },
      MID: {
        posId: 3,
        posKey: 'MID',
        label: 'Midfielders',
        target: midBase,
        adjustedTarget: midAdjusted,
        spent: midSpent,
        rolloverFromPrev: defRollover,
        rolloverToNext: midRollover,
        remainingInPhase: Math.max(0, Math.round((midAdjusted - midSpent) * 100) / 100),
      },
      FWD: {
        posId: 4,
        posKey: 'FWD',
        label: 'Forwards',
        target: fwdBase,
        adjustedTarget: fwdAdjusted,
        spent: fwdSpent,
        rolloverFromPrev: midRollover,
        rolloverToNext: 0,
        remainingInPhase: Math.max(0, Math.round(state.budget * 100) / 100),
      }
    };
  }, [state.posBudgets, state.targetFormation, spentByPos, state.budget]);

  // Base list of players for current position in STRICT AUCTION ORDER (Cost desc, Total Points desc)
  const positionPlayersInAuctionOrder = useMemo(() => {
    const list = players.filter(p => p.positionId === state.activePosition);
    return [...list].sort((a, b) => {
      const costA = parseFloat(a.originalCost) || 0;
      const costB = parseFloat(b.originalCost) || 0;
      if (costB !== costA) {
        return costB - costA;
      }
      const ptsA = parseInt(a.totalPoints, 10) || 0;
      const ptsB = parseInt(b.totalPoints, 10) || 0;
      return ptsB - ptsA;
    }).map((player, index) => ({
      ...player,
      lotNumber: index + 1,
    }));
  }, [players, state.activePosition]);

  const targetCountInPosition = useMemo(() => {
    return positionPlayersInAuctionOrder.filter(p => {
      const tag = state.playerTags[p.playerId]?.tag;
      return tag === 'target' || tag === 'backup';
    }).length;
  }, [positionPlayersInAuctionOrder, state.playerTags]);

  const availableCountInPosition = useMemo(() => {
    return positionPlayersInAuctionOrder.filter(p => !squadPlayerIds.has(p.playerId) && !soldPlayerIds.has(p.playerId)).length;
  }, [positionPlayersInAuctionOrder, squadPlayerIds, soldPlayerIds]);

  const filteredPlayers = useMemo(() => {
    let list = positionPlayersInAuctionOrder;

    // Filter mode
    if (filterMode === 'targets') {
      list = list.filter(p => {
        const tag = state.playerTags[p.playerId]?.tag;
        return tag === 'target' || tag === 'backup';
      });
    } else if (filterMode === 'available') {
      list = list.filter(p => !squadPlayerIds.has(p.playerId) && !soldPlayerIds.has(p.playerId));
    }

    // Filter by search
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(p => {
        const team = getTeamById(p.teamId);
        return p.lastName?.toLowerCase().includes(lower) ||
               (team?.name?.toLowerCase().includes(lower)) ||
               (team?.name_short?.toLowerCase().includes(lower));
      });
    }

    // STRICT AUCTION ORDER is preserved!
    return list;
  }, [positionPlayersInAuctionOrder, filterMode, searchTerm, state.playerTags, squadPlayerIds, soldPlayerIds, getTeamById]);

  // --- RECOMMENDATIONS (Contextual Positional Budget Advice) ---
  const recommendations = useMemo(() => {
    const recs = [];
    const posKey = POS_MAP[state.activePosition];
    const needed = posNeeded[posKey] || 0;
    const currentPhaseFlow = budgetWaterfall[posKey];

    if (needed <= 0) {
      recs.push({ type: 'info', text: `You have filled all required ${POS_LABELS[state.activePosition]} slots (${posCounts[posKey]}/${FORMATIONS[state.targetFormation][posKey]}). Save your remaining ${formatBudget(state.budget)} for other positions.` });
      return recs;
    }

    // Positional budget banner
    let budgetText = `Phase Budget: ${formatBudget(currentPhaseFlow.remainingInPhase)} available for ${posKey}s (Base: ${formatBudget(currentPhaseFlow.target)}`;
    if (currentPhaseFlow.rolloverFromPrev > 0) {
      budgetText += ` +${formatBudget(currentPhaseFlow.rolloverFromPrev)} rolled over from earlier phases)`;
    } else if (currentPhaseFlow.rolloverFromPrev < 0) {
      budgetText += ` -${formatBudget(Math.abs(currentPhaseFlow.rolloverFromPrev))} deficit from earlier overspend)`;
    } else {
      budgetText += ')';
    }

    recs.push({
      type: 'budget',
      text: budgetText,
      detail: `Slots needed: ${needed} more ${posKey}(s). Total bank remaining: ${formatBudget(state.budget)}. Safe max bid on any single player: ${formatBudget(maxBidAllowed)}.`,
    });

    // Find available targets in auction order
    const availableTargetsInOrder = positionPlayersInAuctionOrder
      .filter(p => !squadPlayerIds.has(p.playerId) && !soldPlayerIds.has(p.playerId))
      .map(p => ({
        player: p,
        tag: state.playerTags[p.playerId]?.tag,
        maxBid: state.playerTags[p.playerId]?.maxBid,
        research: getResearchData(p),
      }))
      .filter(item => item.tag === 'target' || item.tag === 'backup');

    const nextTarget = availableTargetsInOrder.find(item => item.tag === 'target');
    const nextBackup = availableTargetsInOrder.find(item => item.tag === 'backup');

    if (nextTarget) {
      const bidMax = Math.min(nextTarget.maxBid || MIN_BID, maxBidAllowed);
      recs.push({
        type: 'target',
        text: `🎯 Next Target Up: Lot #${nextTarget.player.lotNumber} ${nextTarget.player.lastName} (${getTeamById(nextTarget.player.teamId)?.name_short} £${nextTarget.player.originalCost}m FPL) — Target Ceiling: ${formatBudget(bidMax)}`,
        detail: nextTarget.research?.notes,
      });
    }

    if (nextBackup) {
      const bidMax = Math.min(nextBackup.maxBid || MIN_BID, maxBidAllowed);
      recs.push({
        type: 'backup',
        text: `🟡 Next Backup Up: Lot #${nextBackup.player.lotNumber} ${nextBackup.player.lastName} (${getTeamById(nextBackup.player.teamId)?.name_short} £${nextBackup.player.originalCost}m FPL) — Target Ceiling: ${formatBudget(bidMax)}`,
        detail: nextBackup.research?.notes,
      });
    }

    return recs;
  }, [state.activePosition, posNeeded, posCounts, state.targetFormation, positionPlayersInAuctionOrder, squadPlayerIds, soldPlayerIds, maxBidAllowed, state.playerTags, budgetWaterfall, state.budget, getTeamById]);

  // --- ACTIONS ---
  const setTag = useCallback((playerId, tag) => {
    setState(prev => {
      const current = prev.playerTags[playerId] || {};
      const research = getResearchData(players.find(p => p.playerId === playerId));
      const newTags = {
        ...prev.playerTags,
        [playerId]: {
          ...current,
          tag: current.tag === tag ? null : tag,
          maxBid: current.maxBid || research?.maxBid || MIN_BID,
          notes: current.notes || research?.notes || '',
        }
      };
      return { ...prev, playerTags: newTags };
    });
  }, [players]);

  const setMaxBid = useCallback((playerId, bid) => {
    setState(prev => {
      const current = prev.playerTags[playerId] || {};
      return {
        ...prev,
        playerTags: {
          ...prev.playerTags,
          [playerId]: { ...current, maxBid: parseFloat(bid) || MIN_BID }
        }
      };
    });
  }, []);

  const setNotes = useCallback((playerId, notes) => {
    setState(prev => {
      const current = prev.playerTags[playerId] || {};
      return {
        ...prev,
        playerTags: {
          ...prev.playerTags,
          [playerId]: { ...current, notes }
        }
      };
    });
  }, []);

  const openBuyDialog = useCallback((player) => {
    const tag = state.playerTags[player.playerId];
    setBuyDialogPlayer(player);
    setBuyAmount(tag?.maxBid?.toString() || MIN_BID.toString());
    setBuyDialogOpen(true);
  }, [state.playerTags]);

  const confirmBuy = useCallback(() => {
    if (!buyDialogPlayer) return;
    const amount = parseFloat(buyAmount);
    if (isNaN(amount) || amount < MIN_BID) {
      setSnackbar({ open: true, message: `Minimum bid is ${formatBudget(MIN_BID)}`, severity: 'error' });
      return;
    }
    if (amount > state.budget) {
      setSnackbar({ open: true, message: `Over budget! You only have ${formatBudget(state.budget)}`, severity: 'error' });
      return;
    }

    setState(prev => ({
      ...prev,
      budget: Math.round((prev.budget - amount) * 100) / 100,
      mySquad: [...prev.mySquad, { playerId: buyDialogPlayer.playerId, bidAmount: amount }],
      auctionStarted: true,
    }));
    setSnackbar({ open: true, message: `Bought ${buyDialogPlayer.lastName} for ${formatBudget(amount)}!`, severity: 'success' });
    setBuyDialogOpen(false);
    setBuyDialogPlayer(null);
  }, [buyDialogPlayer, buyAmount, state.budget]);

  const markSold = useCallback((playerId) => {
    setState(prev => ({
      ...prev,
      soldPlayers: [...prev.soldPlayers, playerId],
      auctionStarted: true,
    }));
    const player = players.find(p => p.playerId === playerId);
    setSnackbar({ open: true, message: `${player?.lastName} sold to another manager`, severity: 'info' });
  }, [players]);

  const undoBuy = useCallback((playerId) => {
    setState(prev => {
      const purchase = prev.mySquad.find(s => s.playerId === playerId);
      return {
        ...prev,
        budget: Math.round((prev.budget + (purchase?.bidAmount || 0)) * 100) / 100,
        mySquad: prev.mySquad.filter(s => s.playerId !== playerId),
      };
    });
    setSnackbar({ open: true, message: 'Purchase undone', severity: 'warning' });
  }, []);

  const undoSold = useCallback((playerId) => {
    setState(prev => ({
      ...prev,
      soldPlayers: prev.soldPlayers.filter(id => id !== playerId),
    }));
  }, []);

  const resetAll = useCallback(() => {
    if (window.confirm('Reset ALL auction data? This cannot be undone.')) {
      setState(getDefaultState());
      localStorage.removeItem(STORAGE_KEY);
      setSnackbar({ open: true, message: 'All auction data reset', severity: 'warning' });
    }
  }, []);

  const changeFormation = useCallback((_, newFormation) => {
    if (newFormation) {
      setState(prev => ({ ...prev, targetFormation: newFormation }));
    }
  }, []);

  const changePosition = useCallback((_, newPos) => {
    setState(prev => ({ ...prev, activePosition: newPos }));
  }, []);


  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', pb: 8 }}>
      {/* ═══ BUDGET BAR ═══ */}
      <Paper elevation={3} sx={{ p: 1.5, mb: 1, position: 'sticky', top: 0, zIndex: 100, bgcolor: 'background.paper' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <GavelIcon color="primary" /> Auction Helper
          </Typography>
          <Tooltip title="Reset all data">
            <IconButton size="small" onClick={resetAll} color="error"><RestartAltIcon /></IconButton>
          </Tooltip>
        </Stack>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2" fontWeight="bold">
                Budget: <span style={{ color: state.budget < 2 ? '#c62828' : state.budget < 5 ? '#e65100' : '#2e7d32', fontSize: '1.1em' }}>
                  {formatBudget(state.budget)}
                </span>
              </Typography>
              <Typography variant="body2">
                Squad: <strong>{state.mySquad.length}/{SQUAD_SIZE}</strong>
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={(state.budget / TOTAL_BUDGET) * 100}
              sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
              color={state.budget < 2 ? 'error' : state.budget < 5 ? 'warning' : 'success'}
            />
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
          {['GKP', 'DEF', 'MID', 'FWD'].map(pos => {
            const need = posNeeded[pos];
            const have = posCounts[pos];
            const target = FORMATIONS[state.targetFormation][pos];
            return (
              <Chip
                key={pos}
                size="small"
                label={`${pos}: ${have}/${target}`}
                color={have >= target ? 'success' : 'default'}
                variant={need > 0 ? 'outlined' : 'filled'}
                icon={have >= target ? <CheckCircleIcon /> : <PersonIcon />}
                sx={{ fontSize: '0.75rem' }}
              />
            );
          })}
          <Chip
            size="small"
            label={`Max bid: ${formatBudget(maxBidAllowed)}`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}
          />
        </Stack>

        {/* Formation selector */}
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Formation:</Typography>
          <ToggleButtonGroup size="small" value={state.targetFormation} exclusive onChange={changeFormation}>
            <ToggleButton value="442" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>4-4-2</ToggleButton>
            <ToggleButton value="451" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>4-5-1</ToggleButton>
            <ToggleButton value="433" sx={{ py: 0, px: 1, fontSize: '0.75rem' }}>4-3-3</ToggleButton>
          </ToggleButtonGroup>
          {state.mySquad.length === SQUAD_SIZE && (
            isFormationValid(posCounts)
              ? <Chip size="small" icon={<CheckCircleIcon />} label={`${getFormationString(posCounts)} ✓`} color="success" />
              : <Chip size="small" icon={<ErrorIcon />} label={`${getFormationString(posCounts)} ✗`} color="error" />
          )}
        </Stack>

        {/* ═══ POSITIONAL BUDGET WATERFALL (BALANCED BLUEPRINT FLOW) ═══ */}
        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e0e0e0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
              ⚖️ Positional Budget Allocation & Rollover:
            </Typography>
            <Button size="small" sx={{ fontSize: '0.65rem', py: 0, px: 0.5, minWidth: 'auto' }} onClick={() => setEditBudgetsOpen(true)}>
              Edit Targets
            </Button>
          </Stack>
          <Grid container spacing={0.5}>
            {['GKP', 'DEF', 'MID', 'FWD'].map((pos, idx) => {
              const flow = budgetWaterfall[pos];
              const isCurrent = state.activePosition === (idx + 1);
              const isPast = state.activePosition > (idx + 1);
              const hasRollover = flow.rolloverFromPrev !== 0;

              return (
                <Grid item xs={3} key={pos}>
                  <Paper
                    elevation={isCurrent ? 2 : 0}
                    onClick={() => setState(prev => ({ ...prev, activePosition: idx + 1 }))}
                    sx={{
                      p: 0.5,
                      cursor: 'pointer',
                      border: isCurrent ? `2px solid ${POS_COLORS[idx+1]}` : '1px solid #e0e0e0',
                      bgcolor: isCurrent ? '#ffffff' : isPast ? '#f5f5f5' : '#fafafa',
                      borderRadius: 1,
                      textAlign: 'center',
                      transition: 'all 0.2s',
                      '&:hover': { bgcolor: '#f0f0f0' }
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', color: POS_COLORS[idx+1], fontSize: '0.7rem', lineHeight: 1.1 }}>
                      {idx+1}. {pos}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', lineHeight: 1.2 }}>
                      {formatBudget(flow.spent)} / {formatBudget(flow.adjustedTarget)}
                    </Typography>
                    {isPast ? (
                      <Chip
                        size="small"
                        label={flow.rolloverToNext >= 0 ? `+${formatBudget(flow.rolloverToNext)} roll` : `-${formatBudget(Math.abs(flow.rolloverToNext))} over`}
                        color={flow.rolloverToNext >= 0 ? 'success' : 'error'}
                        sx={{ height: 16, fontSize: '0.55rem', mt: 0.25, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    ) : isCurrent && hasRollover ? (
                      <Typography variant="caption" sx={{ fontSize: '0.55rem', color: flow.rolloverFromPrev >= 0 ? 'success.main' : 'error.main', fontWeight: 'bold', display: 'block', lineHeight: 1 }}>
                        {flow.rolloverFromPrev >= 0 ? `+${formatBudget(flow.rolloverFromPrev)} roll` : `-${formatBudget(Math.abs(flow.rolloverFromPrev))} deficit`}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.55rem', display: 'block', lineHeight: 1 }}>
                        Base: {formatBudget(flow.target)}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Paper>

      {/* ═══ POSITION TABS ═══ */}
      <Tabs
        value={state.activePosition}
        onChange={changePosition}
        variant="fullWidth"
        sx={{ mb: 1, bgcolor: 'background.paper', borderRadius: 1 }}
      >
        {[1, 2, 3, 4].map(posId => (
          <Tab
            key={posId}
            value={posId}
            label={
              <Badge badgeContent={posNeeded[POS_MAP[posId]]} color="error" max={9}>
                <Typography variant="body2" sx={{ fontWeight: state.activePosition === posId ? 'bold' : 'normal' }}>
                  {POS_MAP[posId]}
                </Typography>
              </Badge>
            }
            sx={{ minHeight: 42, py: 0.5 }}
          />
        ))}
      </Tabs>

      {/* ═══ RECOMMENDATIONS PANEL ═══ */}
      {recommendations.length > 0 && (
        <Accordion expanded={showRecommendations} onChange={() => setShowRecommendations(!showRecommendations)} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon color="primary" fontSize="small" /> Recommendations
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={0.5}>
              {recommendations.map((rec, i) => (
                <Alert
                  key={i}
                  severity={rec.type === 'target' ? 'success' : rec.type === 'backup' ? 'warning' : rec.type === 'budget' ? 'info' : 'info'}
                  sx={{ py: 0, '& .MuiAlert-message': { py: 0.5 } }}
                >
                  <Typography variant="body2" fontWeight="bold">{rec.text}</Typography>
                  {rec.detail && <Typography variant="caption" color="text.secondary">{rec.detail}</Typography>}
                </Alert>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* ═══ CONTROLS (FILTER & SEARCH) ═══ */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems={{ xs: 'stretch', sm: 'center' }}>
        <ToggleButtonGroup
          size="small"
          value={filterMode}
          exclusive
          onChange={(_, val) => val && setFilterMode(val)}
          sx={{ bgcolor: 'background.paper', flexShrink: 0 }}
        >
          <ToggleButton value="all" sx={{ py: 0.5, px: 1.2, fontSize: '0.75rem' }}>
            Auction Order ({positionPlayersInAuctionOrder.length})
          </ToggleButton>
          <ToggleButton value="targets" sx={{ py: 0.5, px: 1.2, fontSize: '0.75rem' }}>
            ⭐ Targets ({targetCountInPosition})
          </ToggleButton>
          <ToggleButton value="available" sx={{ py: 0.5, px: 1.2, fontSize: '0.75rem' }}>
            Available ({availableCountInPosition})
          </ToggleButton>
        </ToggleButtonGroup>

        <TextField
          fullWidth
          size="small"
          placeholder="Search player or team..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
      </Stack>

      {/* ═══ PLAYER LIST (STRICT AUCTION ORDER) ═══ */}
      <TableContainer component={Paper} sx={{ mb: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ py: 0.5, px: 0.5, width: 32, textAlign: 'center' }}>#</TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5, width: 36 }}></TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5 }}>Player</TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5, width: 55 }}>Cost</TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5, width: 40 }}>Pts</TableCell>
              <TableCell sx={{ py: 0.5, px: 0.5, width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPlayers.map((player) => {
              const tag = state.playerTags[player.playerId];
              const research = getResearchData(player);
              const isBought = squadPlayerIds.has(player.playerId);
              const isSold = soldPlayerIds.has(player.playerId);
              const team = getTeamById(player.teamId);
              const pos = getPlayerPositionById(player.positionId);
              const isUnavailable = isBought || isSold;

              return (
                <React.Fragment key={player.playerId}>
                  <TableRow
                    sx={{
                      opacity: isUnavailable ? 0.45 : 1,
                      bgcolor: isBought ? '#e8f5e9' : isSold ? '#fafafa' :
                               tag?.tag === 'target' ? 'rgba(46, 125, 50, 0.08)' :
                               tag?.tag === 'backup' ? 'rgba(237, 108, 2, 0.08)' :
                               tag?.tag === 'avoid' ? 'rgba(198, 40, 40, 0.08)' : 'inherit',
                      '&:hover': { bgcolor: isUnavailable ? undefined : '#f5f5f5' },
                      borderLeft: tag?.tag === 'target' ? '4px solid #2e7d32' :
                                  tag?.tag === 'backup' ? '4px solid #ed6c02' :
                                  tag?.tag === 'avoid' ? '4px solid #c62828' : '4px solid transparent',
                    }}
                  >
                    <TableCell sx={{ py: 0.25, px: 0.5, textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                        #{player.lotNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>
                      <PlayerShirtImage player={player} style={{ width: 32, height: 32 }} />
                    </TableCell>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {player.lastName}
                        {tag?.tag === 'target' && !isUnavailable && (
                          <Chip size="small" label="TARGET" color="success" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                        )}
                        {tag?.tag === 'backup' && !isUnavailable && (
                          <Chip size="small" label="BACKUP" color="warning" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                        )}
                        {tag?.tag === 'avoid' && !isUnavailable && (
                          <Chip size="small" label="AVOID" color="error" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 'bold' }} />
                        )}
                        {isBought && <Chip size="small" label="MINE" color="success" sx={{ height: 18, fontSize: '0.65rem' }} />}
                        {isSold && <Chip size="small" label="SOLD" color="default" sx={{ height: 18, fontSize: '0.65rem' }} />}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1 }}>
                        <strong style={{ color: POS_COLORS[player.positionId] }}>{pos?.name_short}</strong> {team?.name_short}
                        {research && (
                          <span style={{ color: getValueColor(research.value), fontWeight: 'bold', marginLeft: 4 }}>
                            [{research.value.charAt(0).toUpperCase()}]
                          </span>
                        )}
                      </Typography>
                      {player.news && (
                        <Typography variant="caption" color="error" sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}>
                          ⚠️ {player.news}
                        </Typography>
                      )}
                      {tag?.notes && !player.news && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1, fontSize: '0.65rem' }}>
                          {tag.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>
                      <Typography variant="body2">£{player.originalCost}m</Typography>
                      {tag?.maxBid > 0 && !isUnavailable && (
                        <Typography variant="caption" color="primary" fontWeight="bold" sx={{ display: 'block', fontSize: '0.65rem' }}>
                          max {formatBudget(tag.maxBid)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>
                      <Typography variant="body2">{player.totalPoints}</Typography>
                    </TableCell>
                    <TableCell sx={{ py: 0.25, px: 0.5 }}>
                      {!isUnavailable ? (
                        <Stack direction="row" spacing={0} flexWrap="wrap">
                          <Tooltip title="Target (⭐)">
                            <IconButton size="small" onClick={() => setTag(player.playerId, 'target')}
                              sx={{ color: tag?.tag === 'target' ? '#2e7d32' : '#bdbdbd', p: 0.25 }}>
                              {tag?.tag === 'target' ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Backup (🛡️)">
                            <IconButton size="small" onClick={() => setTag(player.playerId, 'backup')}
                              sx={{ color: tag?.tag === 'backup' ? '#ed6c02' : '#bdbdbd', p: 0.25 }}>
                              {tag?.tag === 'backup' ? <BookmarkIcon fontSize="small" /> : <BookmarkBorderIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Avoid (🚫)">
                            <IconButton size="small" onClick={() => setTag(player.playerId, 'avoid')}
                              sx={{ color: tag?.tag === 'avoid' ? '#c62828' : '#bdbdbd', p: 0.25 }}>
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="I bought this">
                            <IconButton size="small" onClick={() => openBuyDialog(player)}
                              sx={{ color: '#1565c0', p: 0.25 }}>
                              <ShoppingCartIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Sold to another manager">
                            <IconButton size="small" onClick={() => markSold(player.playerId)}
                              sx={{ color: '#6d6d6d', p: 0.25 }}>
                              <SportsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Tooltip title="Undo">
                          <IconButton size="small" onClick={() => isBought ? undoBuy(player.playerId) : undoSold(player.playerId)}
                            sx={{ p: 0.25 }}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Editable max bid row for tagged players (Targets AND Backups) */}
                  {(tag?.tag === 'target' || tag?.tag === 'backup') && !isUnavailable && (
                    <TableRow sx={{ bgcolor: tag?.tag === 'target' ? 'rgba(46, 125, 50, 0.04)' : 'rgba(237, 108, 2, 0.04)' }}>
                      <TableCell colSpan={6} sx={{ py: 0.5, px: 1, borderBottom: 'none' }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" sx={{
                            fontWeight: 'bold',
                            color: tag?.tag === 'target' ? '#2e7d32' : '#ed6c02',
                            minWidth: 70
                          }}>
                            {tag?.tag === 'target' ? '⭐ Target Max:' : '🛡️ Backup Max:'}
                          </Typography>
                          <TextField
                            size="small"
                            type="number"
                            value={tag.maxBid ?? ''}
                            onChange={(e) => setMaxBid(player.playerId, e.target.value)}
                            inputProps={{ step: BID_INCREMENT, min: MIN_BID, max: TOTAL_BUDGET, style: { padding: '2px 8px', fontSize: '0.8rem' } }}
                            InputProps={{ startAdornment: <InputAdornment position="start" sx={{ '& .MuiTypography-root': { fontSize: '0.75rem' } }}>£</InputAdornment> }}
                            sx={{ width: 95 }}
                          />
                          <TextField
                            size="small"
                            placeholder="Tactical notes (e.g. sequence strategy, auction order priority)..."
                            value={tag.notes || ''}
                            onChange={(e) => setNotes(player.playerId, e.target.value)}
                            inputProps={{ style: { padding: '2px 8px', fontSize: '0.8rem' } }}
                            sx={{ flex: 1 }}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ═══ MY SQUAD PANEL ═══ */}
      <Accordion defaultExpanded sx={{ mb: 1 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <PersonIcon fontSize="small" /> My Squad ({state.mySquad.length}/{SQUAD_SIZE})
            {state.mySquad.length > 0 && (
              <Chip size="small" label={`Spent: ${formatBudget(TOTAL_BUDGET - state.budget)}`} sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />
            )}
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {state.mySquad.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No players purchased yet. Use the 🛒 button to record purchases.
            </Typography>
          ) : (
            <>
              {['GKP', 'DEF', 'MID', 'FWD'].map(pos => {
                const posId = Object.keys(POS_MAP).find(k => POS_MAP[k] === pos);
                const squadInPos = state.mySquad.filter(s => {
                  const p = players.find(pl => pl.playerId === s.playerId);
                  return p && POS_MAP[p.positionId] === pos;
                });
                const target = FORMATIONS[state.targetFormation][pos];

                return (
                  <Box key={pos} sx={{ mb: 1 }}>
                    <Typography variant="caption" fontWeight="bold" color={POS_COLORS[posId]}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {pos} ({squadInPos.length}/{target})
                      {squadInPos.length >= target && <CheckCircleIcon sx={{ fontSize: 14, color: '#2e7d32' }} />}
                    </Typography>
                    {squadInPos.map(s => {
                      const p = players.find(pl => pl.playerId === s.playerId);
                      if (!p) return null;
                      const team = getTeamById(p.teamId);
                      return (
                        <Stack key={s.playerId} direction="row" alignItems="center" spacing={0.5} sx={{ pl: 1 }}>
                          <PlayerShirtImage player={p} style={{ width: 24, height: 24 }} />
                          <Typography variant="body2" sx={{ flex: 1 }}>{p.lastName} <Typography component="span" variant="caption" color="text.secondary">({team?.name_short})</Typography></Typography>
                          <Typography variant="caption" color="primary" fontWeight="bold">{formatBudget(s.bidAmount)}</Typography>
                          <IconButton size="small" onClick={() => undoBuy(s.playerId)} sx={{ p: 0 }}>
                            <DeleteIcon sx={{ fontSize: 16 }} color="error" />
                          </IconButton>
                        </Stack>
                      );
                    })}
                    {Array.from({ length: Math.max(0, target - squadInPos.length) }).map((_, i) => (
                      <Typography key={i} variant="caption" color="text.disabled" sx={{ pl: 1, display: 'block' }}>
                        — empty slot —
                      </Typography>
                    ))}
                  </Box>
                );
              })}
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Formation: <strong>{getFormationString(posCounts)}</strong></Typography>
                <Typography variant="body2">FFP: <strong>£{state.mySquad.reduce((sum, s) => {
                  const p = players.find(pl => pl.playerId === s.playerId);
                  return sum + (p?.originalCost || 0);
                }, 0).toFixed(1)}m</strong></Typography>
              </Stack>
            </>
          )}
        </AccordionDetails>
      </Accordion>

      {/* ═══ BUY DIALOG ═══ */}
      <Dialog open={buyDialogOpen} onClose={() => setBuyDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Record Purchase
        </DialogTitle>
        <DialogContent>
          {buyDialogPlayer && (
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <PlayerShirtImage player={buyDialogPlayer} style={{ width: 48, height: 48 }} />
                <Box>
                  <Typography variant="h6">{buyDialogPlayer.lastName}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {getPlayerPositionById(buyDialogPlayer.positionId)?.name_short} — {getTeamById(buyDialogPlayer.teamId)?.name} — £{buyDialogPlayer.originalCost}m
                  </Typography>
                </Box>
              </Stack>
              <TextField
                autoFocus
                fullWidth
                label="Winning bid amount (£)"
                type="number"
                value={buyAmount}
                onChange={(e) => setBuyAmount(e.target.value)}
                inputProps={{ step: BID_INCREMENT, min: MIN_BID, max: state.budget }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                sx={{ mb: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Budget remaining after: {formatBudget(state.budget - (parseFloat(buyAmount) || 0))}
              </Typography>
              {parseFloat(buyAmount) > maxBidAllowed && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  This exceeds your safe max bid of {formatBudget(maxBidAllowed)}. You may not be able to fill remaining slots.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBuyDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmBuy} variant="contained" color="success" startIcon={<ShoppingCartIcon />}>
            Confirm Purchase
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ EDIT POSITIONAL BUDGETS DIALOG ═══ */}
      <Dialog open={editBudgetsOpen} onClose={() => setEditBudgetsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          Customise Positional Budgets
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set your target allocation for each position (Formation: {getFormationString(posCounts || { DEF: 4, MID: 4, FWD: 2 })}). Unspent money automatically rolls forward to subsequent rounds as the auction progresses!
          </Typography>
          <Stack spacing={1.5}>
            {['GKP', 'DEF', 'MID', 'FWD'].map(pos => (
              <TextField
                key={pos}
                label={`${pos} Baseline Target (£)`}
                type="number"
                size="small"
                value={tempBudgets[pos] ?? ''}
                onChange={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setTempBudgets(prev => ({ ...prev, [pos]: val }));
                }}
                inputProps={{ step: BID_INCREMENT, min: MIN_BID, max: TOTAL_BUDGET }}
                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
              />
            ))}
            <Box sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="body2" fontWeight="bold">Total Allocated:</Typography>
                <Typography variant="body2" fontWeight="bold" sx={{
                  color: Math.abs((tempBudgets.GKP + tempBudgets.DEF + tempBudgets.MID + tempBudgets.FWD) - TOTAL_BUDGET) < 0.01 ? '#2e7d32' : '#c62828',
                  fontSize: '1.05rem'
                }}>
                  £{(tempBudgets.GKP + tempBudgets.DEF + tempBudgets.MID + tempBudgets.FWD).toFixed(2)} / £20.00
                </Typography>
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            const defaults = DEFAULT_POSITION_BUDGETS[state.targetFormation] || { GKP: 0.75, DEF: 3.50, MID: 10.00, FWD: 5.75 };
            setTempBudgets(defaults);
          }}>Reset Defaults</Button>
          <Button onClick={() => setEditBudgetsOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setState(prev => ({ ...prev, posBudgets: tempBudgets }));
              setEditBudgetsOpen(false);
              setSnackbar({ open: true, message: 'Positional target budgets saved!', severity: 'success' });
            }}
          >
            Save Targets
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ SNACKBAR ═══ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
